import { useId, useState } from 'react';
import type { CSSProperties, InputHTMLAttributes, ReactNode } from 'react';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'placeholder'> & {
  etiqueta: string;
  icono?: ReactNode;
  /** Ranura al final del campo. Por ejemplo, mostrar/ocultar la contraseña. */
  derecha?: ReactNode;
  error?: string | undefined;
};

/**
 * Campo con **etiqueta flotante**: el nombre arranca dentro del campo y sube al
 * borde superior al enfocarlo o cuando ya hay algo escrito.
 *
 * Es el mismo patrón de Polaris, y se mantiene por una razón que no es estética:
 * este `Campo` lo van a usar después el constructor de cuestionarios, la carga
 * de nómina y el resto de la aplicación. Si la puerta de entrada tuviera campos
 * de otra familia, se vería como una aplicación distinta de la que hay detrás.
 *
 * ── El radio ──
 * Copathy es una marca de curvas, pero el radio completo convertía el campo en
 * una pastilla y lo hacía parecer un buscador. El punto medio es 1 rem: se lee
 * redondo, conserva el eje horizontal del campo, y es el MISMO radio del botón
 * — un solo lenguaje de esquinas en toda la pantalla, que es lo que hace que
 * deje de hacer ruido.
 *
 * Al enfocar no se pinta halo naranja: se oscurece el borde y se agrega un halo
 * marino al 10 %, que sigue la forma del campo. El naranja se reserva para lo
 * que se puede accionar; un campo enfocado no es una acción.
 */
export function Campo({
  etiqueta,
  icono,
  derecha,
  error,
  className = '',
  id,
  onFocus,
  onBlur,
  ...resto
}: Props) {
  const auto = useId();
  const idInput = id ?? auto;
  const [enfocado, setEnfocado] = useState(false);

  const tieneValor = resto.value != null && String(resto.value).length > 0;
  // date, time y compañía muestran una guía nativa (dd/mm/aaaa) aunque estén
  // vacíos: la etiqueta debe estar SIEMPRE arriba para no encimarse con ella.
  const siempreFlota = ['date', 'time', 'datetime-local', 'month', 'week'].includes(
    resto.type ?? '',
  );
  const flotando = enfocado || tieneValor || siempreFlota;

  const estiloEtiqueta: CSSProperties = flotando
    ? {
        top: '-0.5rem',
        left: '1.125rem',
        transform: 'none',
        fontSize: '0.75rem',
        fontWeight: 600,
        padding: '0 0.375rem',
      }
    : {
        top: '50%',
        left: icono ? '2.875rem' : '1.25rem',
        transform: 'translateY(-50%)',
        fontSize: '0.9375rem',
        fontWeight: 400,
        padding: 0,
      };

  const colorEtiqueta = error
    ? 'text-red-600'
    : flotando
      ? enfocado
        ? 'text-marino-700'
        : 'text-marino-500'
      : 'text-marino-400';

  return (
    /*
      Dos contenedores a propósito: el de fuera es el que una rejilla puede
      estirar; el de dentro se ajusta al input y es el que ancla la etiqueta. Con
      uno solo, un campo al lado de una celda más alta hereda esa altura y el
      `top: 50%` de la etiqueta en reposo la centra en el contenedor estirado
      — se ve descolgada respecto al campo de al lado.
    */
    <div>
      <div className="relative">
        <input
          id={idInput}
          onFocus={(e) => {
            setEnfocado(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setEnfocado(false);
            onBlur?.(e);
          }}
          aria-invalid={error ? true : undefined}
          className={`w-full rounded-2xl border bg-white py-3.5 text-marino-900 outline-none transition
            ${icono ? 'pl-11' : 'pl-4.5'} ${derecha ? 'pr-12' : 'pr-4.5'}
            ${
              error
                ? 'border-red-400 focus:ring-4 focus:ring-red-500/10'
                : 'border-marino-200 hover:border-marino-400 focus:border-marino-700 focus:ring-4 focus:ring-marino-700/10'
            }
            ${className}`}
          {...resto}
        />

        {icono && (
          <span
            className={`pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 transition-colors
              ${enfocado && !error ? 'text-marino-700' : 'text-marino-400'}`}
          >
            {icono}
          </span>
        )}

        <label
          htmlFor={idInput}
          style={estiloEtiqueta}
          className={`pointer-events-none absolute z-10 rounded transition-all duration-150
            ${colorEtiqueta} ${flotando ? 'bg-white' : 'bg-transparent'}`}
        >
          {etiqueta}
        </label>

        {derecha && <span className="absolute right-2 top-1/2 -translate-y-1/2">{derecha}</span>}
      </div>

      {error && <p className="mt-1.5 pl-5 text-xs font-medium text-red-600">{error}</p>}
    </div>
  );
}
